import { useUser } from 'containers/App/context/useUser';
import { useRolesForScholarSystem } from 'containers/scholarSystem';
import { useCountries } from 'containers/useCountries';
import i18n from 'i18next';
import React from 'react';
import 'twin.macro';
import { CheckIcon } from '@heroicons/react/20/solid';
import { AvatarCard } from '@teetsh/app/src/components/AvatarCard';
import Button from '@teetsh/app/src/components/Buttons/Button';
import AutoSuggest from '@teetsh/app/src/components/Form/AutoSuggest/formWrapper';
import ButtonRadio from '@teetsh/app/src/components/Form/ButtonsRadio/formWrapper';
import Input from '@teetsh/app/src/components/Form/InputGroup/formWrapper';
import Select from '@teetsh/app/src/components/Form/Select2/formWrapper';
import { SwitchToggle } from '@teetsh/app/src/components/Form/SwitchToggle';
import TwoColumnsCardSidebar from '@teetsh/app/src/components/TwoColumnsCardSidebar';
import allTz from '@teetsh/app/src/containers/timezones';
import { isValidAndRequiredTimezone } from '@teetsh/app/src/containers/UserSettings/Compte';
import useUpdateUserAccount from '@teetsh/app/src/queries/user/account/useUpdateUserAccount';
import { useUploadPicture } from '@teetsh/app/src/queries/user/useUploadPicture';
import { useResendConfirmationEmail } from '@teetsh/app/src/queries/useSendConfirmEmail';
import { Gender } from '@teetsh/app/src/types/gender';
import { UserAccount, UserLocale } from '@teetsh/app/src/types/user';
import { composeValidators } from '@teetsh/app/src/utils/formUtils';
import { useTranslatedValidation } from '@teetsh/app/src/utils/useTranslatedValidation';
import { Crisp } from 'crisp-sdk-web';
import { FORM_ERROR } from 'final-form';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Chips } from '@teetsh/app/src/components/Chips';
import { UserRole } from '@teetsh/app/src/containers/Demarrage/types';

interface FormPayload {
  email: string;
  givenName: string;
  familyName: string;
  location: string;
  optin: boolean;
  locale: UserLocale;
  gender: Gender;
  disableChat: boolean;
  picture?: string;
  userRoles?: UserRole[];
}

interface Props {
  userAccount: UserAccount;
}

export function GeneralInfos({ userAccount }: Props) {
  const resendConfirmationEmail = useResendConfirmationEmail();
  const onSubmit = useUpdateUserAccount();
  const { t } = useTranslation(['userSettings', 'common', 'onboarding']);
  const { required, isValidEmail } = useTranslatedValidation();
  const { upload: uploadPicture } = useUploadPicture();
  const { emailConfirmed, email } = userAccount;
  const { sortedCountries } = useCountries();
  const user = useUser();
  const scholarSystemValue = user?.country
    ? sortedCountries.find(c => c.code === user.country)?.scholarSystem
    : undefined;

  const roleOptions = useRolesForScholarSystem(scholarSystemValue || 'FR', v =>
    t(`onboarding:${v}`),
  );

  return (
    <TwoColumnsCardSidebar.Section title={t('userSettings:account.title')}>
      <section>
        <div>
          <Form<FormPayload>
            initialValues={{
              email: userAccount.email,
              familyName: userAccount.familyName,
              givenName: userAccount.givenName,
              location: userAccount.location,
              locale: userAccount.locale,
              optin: userAccount.optin,
              gender: userAccount.gender,
              disableChat: userAccount.disableChat,
              picture: userAccount.picture,
              userRoles: (userAccount.userRoles || []) as UserRole[],
            }}
            onSubmit={async values => {
              try {
                await onSubmit(values).then(() => {
                  if (userAccount.locale !== values.locale) {
                    i18n.changeLanguage(values.locale);
                  }
                });
              } catch (err: any) {
                if (err?.response?.status === 409) {
                  return {
                    email: t('userSettings:notifications.emailAlreadyExists'),
                  };
                }
                if (err?.response?.data === 'bad email') {
                  return {
                    email: t('userSettings:notifications.emailInvalid'),
                  };
                }
                return {
                  [FORM_ERROR]: t('common:messages.genericError'),
                };
              }

              return {};
            }}
          >
            {({ handleSubmit, dirty, submitting, submitSucceeded }) => (
              <form onSubmit={handleSubmit}>
                <div tw="mb-8">
                  <div tw="flex mb-3">
                    <div tw="mr-6">
                      <Field<string>
                        name="picture"
                        render={({ input: { onChange, value } }) => (
                          <AvatarCard
                            large
                            onUpload={uploadPicture}
                            onChange={v => onChange(v ? v : 'user')}
                            picture={value === 'user' ? undefined : value}
                          />
                        )}
                      />
                    </div>
                    <div tw="flex-grow">
                      <Field
                        name="givenName"
                        label={t('userSettings:account.givenName')}
                        type="text"
                        validate={required}
                        component={Input}
                      />
                      <div>
                        <Field
                          name="familyName"
                          label={t('userSettings:account.familyName')}
                          type="text"
                          validate={required}
                          component={Input}
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Field
                      name="gender"
                      label={t('userSettings:account.gender.label')}
                      options={[
                        {
                          label: t('userSettings:account.gender.male'),
                          value: 'H',
                        },
                        {
                          label: t('userSettings:account.gender.female'),
                          value: 'F',
                        },
                      ]}
                      component={ButtonRadio}
                    />
                    <Field
                      name="email"
                      validate={composeValidators(required, isValidEmail)}
                      label={t('userSettings:account.email')}
                      type="email"
                      component={Input}
                    />
                    {!emailConfirmed && (
                      <div tw="mb-4 text-warning text-sm">
                        <span>
                          {t('userSettings:account.emailNotConfirmed')}{' '}
                        </span>
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                        <strong
                          tw="cursor-pointer underline block"
                          onClick={() => resendConfirmationEmail({ email })}
                        >
                          {t('userSettings:account.resendConfirmationEmail')}
                        </strong>
                      </div>
                    )}

                    <div tw="mb-3">
                      <Field
                        name="location"
                        label={t('userSettings:account.timezone')}
                        validate={v => isValidAndRequiredTimezone(v, t)}
                        component={AutoSuggest}
                        options={allTz.map(tz => ({ value: tz, label: tz }))}
                      />
                    </div>
                    <div tw="mb-8">
                      <Field
                        name="locale"
                        label={t('userSettings:account.locale')}
                        component={Select}
                        options={[
                          {
                            value: 'fr-FR',
                            label: 'Français 🇫🇷',
                          },
                          {
                            value: 'fr-BE',
                            label: 'Français (Belgique) 🇧🇪',
                          },
                          {
                            value: 'en-US',
                            label: 'English 🇺🇸',
                          },
                          {
                            value: 'en-AU',
                            label: 'English (Australian) 🇦🇺',
                          },
                        ]}
                      />
                    </div>
                    <div tw="mb-8">
                      <p tw="font-semibold mb-4">
                        {t('userSettings:account.statuts')}
                      </p>
                      <Field<UserRole[]>
                        name="userRoles"
                        render={({ input }) => (
                          <Chips<UserRole>
                            options={roleOptions}
                            value={input.value || []}
                            onChange={input.onChange}
                            size="lg"
                          />
                        )}
                      />
                    </div>

                    <p tw="font-semibold mb-4">
                      {t('userSettings:account.options')}
                    </p>

                    <Field
                      name="optin"
                      render={({ input }) => (
                        <SwitchToggle
                          tw="mb-2"
                          label={t('userSettings:account.optinNewsletter')}
                          onChange={input.onChange}
                          checked={input.value}
                        />
                      )}
                    />
                    <Field
                      name="disableChat"
                      render={({ input }) => (
                        <SwitchToggle
                          label={t('userSettings:account.disableCrisp')}
                          onChange={v => {
                            if (v) {
                              Crisp.chat.hide();
                            }
                            if (!v) {
                              Crisp.chat.show();
                            }
                            input.onChange(v);
                          }}
                          checked={input.value}
                        />
                      )}
                    />
                  </div>
                </div>
                <div tw="flex items-center">
                  <Button
                    disabled={!dirty}
                    loading={submitting}
                    intent="primary"
                    type="submit"
                  >
                    {t('common:buttons.save')}
                  </Button>
                  {submitSucceeded && !dirty && (
                    <div tw="text-success font-bold ml-3">
                      <CheckIcon tw="h-5 w-5 mr-2 inline-block" />
                      {t('common:state.saved')}
                    </div>
                  )}
                </div>
              </form>
            )}
          </Form>
        </div>
      </section>
    </TwoColumnsCardSidebar.Section>
  );
}
